import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Button, Card, message, Typography, Space, Divider, Input, Table, Popconfirm, Select
} from 'antd';
import {
    AppstoreOutlined, PlusOutlined, ReloadOutlined
} from '@ant-design/icons';
import {
    mappingDvMayService,
    DmLoaiDichVuDto,
    DmDichVuDto,
    DmMayClsDto,
    MappingDvMayWithNamesDto
} from '../services/mappingDvMayService';

const { Title, Text } = Typography;
const { Option } = Select;

const MayThucHienTab: React.FC = () => {
    const [loaiDichVuList, setLoaiDichVuList] = useState<DmLoaiDichVuDto[]>([]);
    const [dichVuList, setDichVuList] = useState<DmDichVuDto[]>([]);
    const [mayList, setMayList] = useState<DmMayClsDto[]>([]);
    const [mappingList, setMappingList] = useState<MappingDvMayWithNamesDto[]>([]);

    const [mappingLoading, setMappingLoading] = useState(false);
    const [createMappingLoading, setCreateMappingLoading] = useState(false);
    const [dichVuLoading, setDichVuLoading] = useState(false);
    const [mayLoading, setMayLoading] = useState(false);

    const [selectedLoaiDichVu, setSelectedLoaiDichVu] = useState<number | undefined>();
    const [tempSelectedLoaiDichVu, setTempSelectedLoaiDichVu] = useState<number | undefined>();
    const [dichVuSearchText, setDichVuSearchText] = useState<string>('');
    const [tempDichVuSearchText, setTempDichVuSearchText] = useState<string>('');
    const [maySearchText, setMaySearchText] = useState<string>('');
    const [tempMaySearchText, setTempMaySearchText] = useState<string>('');

    const [selectedDichVuKeys, setSelectedDichVuKeys] = useState<React.Key[]>([]);
    const [selectedMayKeys, setSelectedMayKeys] = useState<React.Key[]>([]);

    const [mappingFilterDichVu, setMappingFilterDichVu] = useState<string>('');
    const [mappingFilterMay, setMappingFilterMay] = useState<string>('');
    const [tempMappingFilterDichVu, setTempMappingFilterDichVu] = useState<string>('');
    const [tempMappingFilterMay, setTempMappingFilterMay] = useState<string>('');

    useEffect(() => {
        if (loaiDichVuList.length === 0) loadLoaiDichVu();
        if (dichVuList.length === 0) loadDichVu();
        if (mayList.length === 0) loadMay();
        if (mappingList.length === 0) loadMapping();
    }, []);

    const loadLoaiDichVu = async () => {
        try {
            const list = await mappingDvMayService.getDmLoaiDichVu();
            setLoaiDichVuList(list);
        } catch (error: any) {
            console.error('Load loại dịch vụ error:', error);
            message.error('Không thể tải danh sách loại dịch vụ');
        }
    };

    const loadDichVu = async () => {
        setDichVuLoading(true);
        try {
            const list = await mappingDvMayService.getDmDichVu(selectedLoaiDichVu, dichVuSearchText || undefined);
            setDichVuList(list);
        } catch (error: any) {
            console.error('Load dịch vụ error:', error);
            message.error('Không thể tải danh sách dịch vụ');
        } finally {
            setDichVuLoading(false);
        }
    };

    const loadMay = async () => {
        setMayLoading(true);
        try {
            const list = await mappingDvMayService.getDmMayCls(maySearchText || undefined);
            setMayList(list);
        } catch (error: any) {
            console.error('Load máy error:', error);
            message.error('Không thể tải danh sách máy');
        } finally {
            setMayLoading(false);
        }
    };

    const loadMapping = async () => {
        setMappingLoading(true);
        try {
            const list = await mappingDvMayService.getAllMappingDvMay();
            setMappingList(list);
        } catch (error: any) {
            console.error('Load mapping error:', error);
            message.error('Không thể tải danh sách mapping');
        } finally {
            setMappingLoading(false);
        }
    };

    const handleCreateMapping = async () => {
        if (selectedDichVuKeys.length === 0 || selectedMayKeys.length === 0) {
            message.warning('Vui lòng chọn ít nhất 1 dịch vụ và 1 máy');
            return;
        }

        setCreateMappingLoading(true);
        try {
            const mappings: any[] = [];
            selectedDichVuKeys.forEach(dvKey => {
                selectedMayKeys.forEach(mayKey => {
                    mappings.push({
                        mahh: Number(dvKey),
                        instrumentID: String(mayKey)
                    });
                });
            });

            const result = await mappingDvMayService.batchCreateMappingDvMay(mappings);
            message.success(result.message);
            setSelectedDichVuKeys([]);
            setSelectedMayKeys([]);
            await loadMapping();
        } catch (error: any) {
            console.error('Create mapping error:', error);
            const data = error?.response?.data || {};
            const msg = data.message || data.Message || error?.message || 'Tạo mapping thất bại';
            message.error(msg);
        } finally {
            setCreateMappingLoading(false);
        }
    };

    const handleDeleteMapping = async (id: number) => {
        try {
            await mappingDvMayService.deleteMappingDvMay(id);
            message.success('Xóa mapping thành công');
            await loadMapping();
        } catch (error: any) {
            console.error('Delete mapping error:', error);
            message.error('Xóa mapping thất bại');
        }
    };

    const handleApplyMappingFilter = () => {
        setMappingFilterDichVu(tempMappingFilterDichVu);
        setMappingFilterMay(tempMappingFilterMay);
    };

    const handleClearMappingFilter = () => {
        setTempMappingFilterDichVu('');
        setTempMappingFilterMay('');
        setMappingFilterDichVu('');
        setMappingFilterMay('');
    };

    const handleApplyDichVuFilter = () => {
        setSelectedLoaiDichVu(tempSelectedLoaiDichVu);
        setDichVuSearchText(tempDichVuSearchText);
    };

    const handleClearDichVuFilter = () => {
        setTempSelectedLoaiDichVu(undefined);
        setTempDichVuSearchText('');
        setSelectedLoaiDichVu(undefined);
        setDichVuSearchText('');
    };

    const handleApplyMayFilter = () => {
        setMaySearchText(tempMaySearchText);
    };

    const handleClearMayFilter = () => {
        setTempMaySearchText('');
        setMaySearchText('');
    };

    const isFirstRenderDichVu = useRef(true);
    useEffect(() => {
        if (isFirstRenderDichVu.current) {
            isFirstRenderDichVu.current = false;
            return;
        }
        loadDichVu();
    }, [selectedLoaiDichVu, dichVuSearchText]);

    const isFirstRenderMay = useRef(true);
    useEffect(() => {
        if (isFirstRenderMay.current) {
            isFirstRenderMay.current = false;
            return;
        }
        loadMay();
    }, [maySearchText]);

    const mappedMahhSet = useMemo(() => new Set(mappingList.map(m => m.mahh)), [mappingList]);

    const filteredMappingList = useMemo(() => {
        return mappingList.filter(m => {
            const matchDichVu = !mappingFilterDichVu ||
                m.tenDichVu?.toLowerCase().includes(mappingFilterDichVu.toLowerCase());
            const matchMay = !mappingFilterMay ||
                m.insName?.toLowerCase().includes(mappingFilterMay.toLowerCase());
            return matchDichVu && matchMay;
        });
    }, [mappingList, mappingFilterDichVu, mappingFilterMay]);

    return (
        <>
            <style>{`
                .row-mapped {
                    background-color: #9cd8a4 !important;
                }
            `}</style>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Card>
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <Space align="start" style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                            <Space direction="vertical" size={4}>
                                <Title level={4} style={{ margin: 0 }}>
                                    <AppstoreOutlined style={{ marginRight: 8, color: '#722ed1' }} />
                                    Mapping Máy - Dịch Vụ
                                </Title>
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                    Ghép nối dịch vụ từ HIS với máy thực hiện. Chọn dịch vụ và máy, sau đó bấm "Ghép máy và dịch vụ" để tạo mapping.
                                </Text>
                            </Space>
                            <Space wrap>
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={() => { loadDichVu(); loadMay(); loadMapping(); }}
                                >
                                    Làm mới
                                </Button>
                            </Space>
                        </Space>

                        <Divider style={{ margin: '8px 0' }} />

                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Text strong>Danh sách Dịch Vụ (từ HIS)</Text>
                            <Space style={{ width: '100%' }} size={12}>
                                <Select
                                    placeholder="-- Chọn loại dịch vụ --"
                                    style={{ width: 250 }}
                                    allowClear
                                    value={tempSelectedLoaiDichVu}
                                    onChange={setTempSelectedLoaiDichVu}
                                >
                                    {loaiDichVuList.map(l => (
                                        <Option key={l.maLoaiDichVu} value={l.maLoaiDichVu}>
                                            {l.tenLoaiDichVu}
                                        </Option>
                                    ))}
                                </Select>
                                <Input
                                    placeholder="Tìm kiếm tên dịch vụ..."
                                    style={{ width: 300 }}
                                    value={tempDichVuSearchText}
                                    onChange={(e) => setTempDichVuSearchText(e.target.value)}
                                    onPressEnter={handleApplyDichVuFilter}
                                />
                                <Button onClick={handleApplyDichVuFilter}>Tìm kiếm</Button>
                                <Button onClick={handleClearDichVuFilter}>Xóa lọc</Button>
                            </Space>
                        </Space>

                        <Table
                            rowKey="mahh"
                            size="small"
                            loading={dichVuLoading}
                            dataSource={dichVuList}
                            pagination={{ pageSize: 10 }}
                            rowSelection={{
                                selectedRowKeys: selectedDichVuKeys,
                                onChange: setSelectedDichVuKeys
                            }}
                            rowClassName={(record) => {
                                return mappedMahhSet.has(record.mahh) ? 'row-mapped' : '';
                            }}
                            columns={[
                                {
                                    title: 'Mã HH',
                                    dataIndex: 'mahh',
                                    width: 80,
                                    render: (v: any) => <Text code>{v}</Text>
                                },
                                {
                                    title: 'Tên dịch vụ',
                                    dataIndex: 'tenDichVu',
                                    ellipsis: true
                                },
                                {
                                    title: 'Mã View',
                                    dataIndex: 'maView',
                                    width: 120,
                                    render: (v: any) => v ? <Text code>{v}</Text> : <Text type="secondary">-</Text>
                                }
                            ]}
                            scroll={{ y: 300 }}
                        />

                        <Divider style={{ margin: '8px 0' }} />

                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Text strong>Danh sách Máy Thực Hiện (từ HIS)</Text>
                            <Space style={{ width: '100%' }} size={12}>
                                <Input
                                    placeholder="Tìm kiếm tên máy..."
                                    style={{ width: 300 }}
                                    value={tempMaySearchText}
                                    onChange={(e) => setTempMaySearchText(e.target.value)}
                                    onPressEnter={handleApplyMayFilter}
                                />
                                <Button onClick={handleApplyMayFilter}>Tìm kiếm</Button>
                                <Button onClick={handleClearMayFilter}>Xóa lọc</Button>
                            </Space>
                        </Space>

                        <Table
                            rowKey="instrumentID"
                            size="small"
                            loading={mayLoading}
                            dataSource={mayList}
                            pagination={{ pageSize: 10 }}
                            rowSelection={{
                                selectedRowKeys: selectedMayKeys,
                                onChange: setSelectedMayKeys
                            }}
                            columns={[
                                {
                                    title: 'Instrument ID',
                                    dataIndex: 'instrumentID',
                                    width: 150,
                                    render: (v: any) => <Text code>{v}</Text>
                                },
                                {
                                    title: 'Tên máy',
                                    dataIndex: 'insName',
                                    ellipsis: true
                                },
                                {
                                    title: 'Mã máy',
                                    dataIndex: 'maMay',
                                    width: 100,
                                    render: (v: any) => v ? <Text code>{v}</Text> : <Text type="secondary">-</Text>
                                }
                            ]}
                            scroll={{ y: 300 }}
                        />

                        <Divider style={{ margin: '8px 0' }} />

                        <Space>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleCreateMapping}
                                loading={createMappingLoading}
                                disabled={selectedDichVuKeys.length === 0 || selectedMayKeys.length === 0}
                                style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                            >
                                Ghép máy và dịch vụ ({selectedDichVuKeys.length} dịch vụ × {selectedMayKeys.length} máy = {selectedDichVuKeys.length * selectedMayKeys.length} mapping)
                            </Button>
                        </Space>
                    </Space>
                </Card>

                <Card>
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
                            <Title level={5} style={{ margin: 0 }}>
                                Danh sách Mapping đã tạo ({mappingList.length})
                            </Title>
                            <Space size={8}>
                                <Input
                                    placeholder="Lọc theo tên dịch vụ..."
                                    style={{ width: 200 }}
                                    value={tempMappingFilterDichVu}
                                    onChange={(e) => setTempMappingFilterDichVu(e.target.value)}
                                    onPressEnter={handleApplyMappingFilter}
                                    allowClear
                                />
                                <Input
                                    placeholder="Lọc theo tên máy..."
                                    style={{ width: 200 }}
                                    value={tempMappingFilterMay}
                                    onChange={(e) => setTempMappingFilterMay(e.target.value)}
                                    onPressEnter={handleApplyMappingFilter}
                                    allowClear
                                />
                                <Button onClick={handleApplyMappingFilter}>Tìm kiếm</Button>
                                <Button onClick={handleClearMappingFilter}>Xóa lọc</Button>
                            </Space>
                        </Space>
                        <Table
                            rowKey="id"
                            size="small"
                            loading={mappingLoading}
                            dataSource={filteredMappingList}
                            pagination={{ pageSize: 20 }}
                            columns={[
                                {
                                    title: 'Mã HH (Dịch vụ)',
                                    dataIndex: 'mahh',
                                    width: 100,
                                    render: (v: any) => <Text code>{v}</Text>
                                },
                                {
                                    title: 'Tên dịch vụ',
                                    dataIndex: 'tenDichVu',
                                    ellipsis: true
                                },
                                {
                                    title: 'Instrument ID (Máy)',
                                    dataIndex: 'instrumentID',
                                    width: 150,
                                    render: (v: any) => <Text code>{v}</Text>
                                },
                                {
                                    title: 'Tên máy',
                                    dataIndex: 'insName',
                                    ellipsis: true
                                },
                                {
                                    title: 'Thao tác',
                                    key: 'actions',
                                    width: 80,
                                    render: (_: any, r: MappingDvMayWithNamesDto) => (
                                        <Popconfirm
                                            title="Xóa mapping này?"
                                            onConfirm={() => handleDeleteMapping(r.id)}
                                            okText="Xóa"
                                            cancelText="Hủy"
                                        >
                                            <Button size="small" danger>Xóa</Button>
                                        </Popconfirm>
                                    )
                                }
                            ]}
                            scroll={{ x: 600 }}
                        />
                    </Space>
                </Card>
            </Space>
        </>
    );
};

export default MayThucHienTab;
